import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FlaskConical, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const API = '/api';


// Collapsible horizontal strip for therapeutic areas — shows ~12 by default
// and expands to reveal the rest on demand.
function TherapeuticAreasStrip({ areas }) {
  const [expanded, setExpanded] = useState(false);
  const COLLAPSED = 12;
  const visible = expanded ? areas : areas.slice(0, COLLAPSED);
  const hidden = areas.length - COLLAPSED;
  return (
    <Card className="border border-slate-200 mb-4" data-testid="therapeutic-areas-strip">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">
            Therapeutic Areas <span className="ml-1 text-slate-500 normal-case font-normal">({areas.length})</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {visible.map((a, i) => (
            <Badge key={i} className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200 font-normal">{a}</Badge>
          ))}
          {hidden > 0 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-[10px] text-[#00709B] hover:underline px-2 py-0.5 flex items-center gap-0.5"
              data-testid="ta-expand-toggle"
            >
              {expanded ? <><ChevronUp className="w-2.5 h-2.5" /> Show fewer</> : <><ChevronDown className="w-2.5 h-2.5" /> +{hidden} more</>}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PublicContactMatchesPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/contact-matches/${token}`)
      .then(res => setData(res.data))
      .catch(() => setError('This link is not valid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Copy protection
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.addEventListener('copy', prevent);
    document.addEventListener('cut', prevent);
    document.addEventListener('contextmenu', prevent);
    return () => {
      document.removeEventListener('copy', prevent);
      document.removeEventListener('cut', prevent);
      document.removeEventListener('contextmenu', prevent);
    };
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="max-w-md"><CardContent className="p-8 text-center">
        <p className="text-sm text-slate-500">{error}</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header row — LEFT: title + About ForesightRx | RIGHT: Preferred Stages + Stage Breakdown */}
        <div className="flex flex-col lg:flex-row gap-6 items-start mb-6">
          <div className="flex-1 min-w-0 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical className="w-5 h-5 text-sky-600" />
                <span className="text-xs font-semibold text-sky-600 uppercase tracking-wider">ForesightRx Matching</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Curated Matches for {data.contact_name}
              </h1>
              {data.company_name && (
                <p className="text-sm text-slate-500 mt-1">{data.company_name}</p>
              )}
            </div>
            {/* About ForesightRx — lives directly under the title, same exact copy as /status pages */}
            <Card className="border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-sm" data-testid="about-foresightrx-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00709B] to-sky-700 flex items-center justify-center shrink-0 shadow">
                    <FlaskConical className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-semibold text-[#00709B] uppercase tracking-wider mb-1">About ForesightRx</p>
                    <p className="text-[12px] leading-relaxed text-slate-700">
                      <span className="font-semibold text-slate-900">ForesightRx</span> is the deal advisory arm of Marksman Healthcare, specializing in cross-border business development for life sciences. We connect biotech innovators with the right pharma partners — managing strategic matchmaking, targeted outreach, and licensing support to help early and mid-stage companies scale globally.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Right rail — preferred stages + stage breakdown */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-[340px] shrink-0">
            <Card className="border border-slate-200 flex-1">
              <CardContent className="p-3">
                <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Preferred Stages</p>
                <div className="flex flex-wrap gap-1">
                  {data.preferred_stages?.length ? data.preferred_stages.map((s, i) => (
                    <Badge key={i} className="text-[10px] bg-sky-50 text-sky-700 border border-sky-200">{s}</Badge>
                  )) : <span className="text-[10px] text-slate-400">—</span>}
                </div>
                {data.preferred_modalities?.length > 0 && (
                  <>
                    <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mt-2 mb-1.5">Preferred Modalities</p>
                    <div className="flex flex-wrap gap-1">
                      {data.preferred_modalities.map((m, i) => (
                        <Badge key={i} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">{m}</Badge>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            <Card className="border border-slate-200 flex-1">
              <CardContent className="p-3">
                <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Stage Breakdown</p>
                <div className="space-y-1">
                  {(() => {
                    const stageOrder = ['Commercial', 'Phase 3', 'Phase 2', 'Phase 1', 'IND', 'Clinical Stage', 'Pre-Clinical', 'Research', 'Platform/Technology', 'Unknown'];
                    const entries = Object.entries(data.stage_breakdown || {});
                    entries.sort((a, b) => {
                      const ai = stageOrder.indexOf(a[0]);
                      const bi = stageOrder.indexOf(b[0]);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    });
                    return entries.map(([stage, count]) => (
                      <div key={stage} className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-600">{stage}</span>
                        <span className="font-bold text-slate-800">{count}</span>
                      </div>
                    ));
                  })()}
                </div>
                <div className="border-t border-slate-100 mt-2 pt-1.5 flex justify-between text-xs font-bold">
                  <span className="text-slate-800">Total</span>
                  <span className="text-sky-600">{data.total}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* AI Summary — pulled from actual asset data */}
        {data.ai_summary && (
          <Card className="border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-violet-50 shadow-sm mb-4" data-testid="ai-summary-card">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center shrink-0 shadow">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-semibold text-sky-700 uppercase tracking-wider mb-1">Executive Summary</p>
                  <p className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-line">{data.ai_summary}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Therapeutic Areas — horizontal strip, collapsible if long */}
        {data.interest_areas?.length > 0 && (
          <TherapeuticAreasStrip areas={data.interest_areas} />
        )}

        {/* Results Table — no company name, asset name, or target */}
        {data.matches?.length > 0 ? (
          <Card className="border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 w-[40px]">#</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Stage</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Indication</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Target</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Modality</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const stageOrder = ['Commercial', 'Phase 3', 'Phase 2', 'Phase 1', 'IND', 'Clinical Stage', 'Pre-Clinical', 'Research', 'Platform/Technology', 'Unknown'];
                    const sorted = [...data.matches].sort((a, b) => {
                      const ai = stageOrder.indexOf(a.stage);
                      const bi = stageOrder.indexOf(b.stage);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    });
                    return sorted.map((m, i) => (
                      <TableRow key={i} className="hover:bg-slate-50/50">
                        <TableCell className="py-2 text-xs text-slate-400">{i + 1}</TableCell>
                        <TableCell className="py-2">
                          <Badge className="text-[9px] bg-sky-50 text-sky-700 border border-sky-200">{m.stage}</Badge>
                        </TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600">{m.indication || '—'}</TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600">{m.target || '—'}</TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600">{m.modality || '—'}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : (
          <Card className="border border-slate-200">
            <CardContent className="py-12 text-center">
              <FlaskConical className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No matching assets found</p>
            </CardContent>
          </Card>
        )}

        {/* About ForesightRx moved to the top (before the asset table) */}

        <div className="mt-8 text-center">
          <p className="text-[10px] text-slate-400">Powered by ForesightRx | MarksMan Healthcare</p>
        </div>
      </div>
    </div>
  );
}
